import styled, { keyframes } from 'styled-components';

import ActionButton from 'components/action-button';
import LoyaltyPointsIcon from 'components/icons/loyalty-points-icon';
import { IncentiveEvaluationMessages } from 'components/incentive-evaluation-messages/incentive-evaluation-messages';
import { DisclaimerContainer } from 'components/offers/ui-refresh/offer-disclaimer';
import Picture from 'components/picture';
import { ProgressMeter } from 'components/progress-meter';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { primitive } from 'styles/constants/primitives';
import { fadeIn } from 'utils/style/animations';

import { RewardLock } from '../../reward-lock';
import { HIGHLIGHT_COLOR } from '../constants';
import { RewardLockIcon } from '../reward-lock-icon';

import theme from './theme';

export const Wrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  flex-direction: column;
  animation: ${fadeIn} 0.65s forwards;
  overflow-y: scroll;
  overflow-x: hidden;
  background: ${Styles.color.white};
  width: 100%;
  ${Styles.desktop`
    animation: ${fadeIn} 0.25s forwards;
  `}
`;

export const ImageWrapper = styled.div`
  position: relative;
  height: fit-content;
  width: 100%;
  display: flex;
  justify-content: center;
  padding-bottom: 1rem;
  flex-shrink: 0;
`;

export const Padding = styled.div`
  min-height: 1rem;
  width: 1px;
`;

export const StyledRewardImage = styled(Picture)`
  width: 80%;
  max-width: 34.375rem;
  height: 8rem;
  margin-top: 1rem;
  z-index: ${Styles.zIndex.normal};

  ${Styles.desktop`
    height: fit-content;
    width: 70%;
  `}
`;

export const BackgroundImage = styled(Picture)`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
`;

const slideRight = keyframes`
  from {
    transform: translateX(50px);
  } to {
    transform: translateY(0);
  }
`;

export const RewardName = styled.h2`
  font-size: 2rem;
  font-family: ${Styles.fontFamily.brand};
  line-height: 1;
  margin: 0.5rem 0 0;
  text-align: center;
  max-width: 60rem;
  animation: ${slideRight} 0.5s forwards;
  ${Styles.desktop`
    margin: 1rem 0 0;
    animation: unset;
    font-size: 2.25rem;
  `}
  ${Styles.mobileTiny`
    font-size: 1.7rem;
  `}
`;

export const RewardDescription = styled(SanityBlockRenderer)`
  margin: 0.5rem 0 0;
  font-family: ${Styles.fontFamily.body};
  text-align: center;
  font-size: 0.812rem;
  line-height: 1.3rem;
  color: ${theme.rewardDescriptionColor};
  width: 85%;
  ${Styles.desktop`
    font-size: 1rem;
    line-height: 1.4rem;
    max-width: 45rem;
  `}
`;

export const ButtonContainer = styled.div`
  flex-shrink: 0;
  margin: 1rem 0;

  ${Styles.desktop`
    margin: 2rem 0 0;
  `}
  > * {
    width: 100%;

    :last-child {
      margin-top: 1rem;
    }
  }
`;

export const RewardDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 1rem;
`;

export const DisclaimerText = styled(SanityBlockRenderer)`
  max-width: 36rem;
  text-align: center;
  font-size: 0.75rem;
  font-family: ${Styles.fontFamily.body};
  font-style: normal;
  line-height: 1.33;
  color: ${theme.disclaimerTextColor};
  margin: 1rem auto;
  ${Styles.desktop`
    margin-top: 2rem;
  `}
`;

export const StyledProgressMeter = styled(ProgressMeter)`
  border: 0;
  background: ${primitive.$white};
  width: 15rem;
  margin: 0.5rem 0;
  ${Styles.desktop`
    margin-top: 1.1rem;
  `}
`;

export const StyledRewardLock = styled(RewardLock)`
  transform: scale(0.5);
  > div {
    background-color: ${Styles.color.black};
    ::before,
    ::after {
      border-color: ${Styles.color.black};
    }
    ::after {
      background-color: inherit;
    }
  }
`;

export const LockContainer = styled.div`
  position: relative;
  right: 45px;
`;

export const PointsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 1rem;
  ${Styles.desktop`
    margin-top: 1.5rem;
  `}
`;

export const PointsText = styled.p`
  font-family: ${Styles.fontFamily.brand};
  font-size: 20px;
  margin: 0;
  display: flex;
  align-items: center;
  text-transform: lowercase;
  strong {
    font-weight: ${Styles.fontWeight.heavy};
  }
`;

export const StyledPointsIcon = styled(LoyaltyPointsIcon)`
  transform: scale(1.4);
  margin: 0 0.5rem 0 0;
  path {
    fill: ${Styles.color.success};
  }
`;

export const ErrorMessage = styled.p`
  text-align: center;
  color: ${Styles.color.error};
`;

export const RewardLabel = styled.div`
  background-color: ${HIGHLIGHT_COLOR};
  padding: 0.4rem 0.6rem 0.5rem 0.6rem;
  border-radius: 0.5rem;
  color: ${Styles.color.white};
  margin: 0.3rem 0.4rem 0 0.4rem;
  font-size: 0.9rem;
  line-height: 1;
  ${Styles.desktop`
    display: none;
  `}
`;

export const StyledRewardLockIcon = styled(RewardLockIcon)`
  & > div > div {
    background-color: ${HIGHLIGHT_COLOR};

    &::before,
    &::after {
      border-color: ${HIGHLIGHT_COLOR};
    }
  }
`;

export const RemoveRewardsButton = ActionButton;

export const StyledIncentiveEvaluationMessages = styled(IncentiveEvaluationMessages)`
  text-align: center;
`;

export const ButtonsWrapper = styled.div`
  position: sticky;
  bottom: 0;
  background-color: ${Styles.color.white};
  display: flex;
  flex-flow: row wrap;
  flex-wrap: wrap;
  flex-direction: column;
  align-items: center;
  z-index: ${Styles.zIndex.top};
  padding: 0 1rem;
  ${Styles.desktop`
    bottom: auto;
    position: static;
    background-color: transparent;
    width: auto;
    display: block;
    padding: 0 1rem 2rem;
    z-index: auto;
  `}
`;

export const StyledDisclaimerContainer = styled(DisclaimerContainer)`
  margin-top: 1rem;
`;
